import React, { useCallback, useEffect, useMemo, useState } from "react";
import MUIDataTable from "mui-datatables";
import { Button, IconButton, Dialog, DialogActions, DialogContent, Box, Chip, Switch } from "@mui/material";
import { Edit, DeleteOutline, Close, ContentCopy, ReceiptLong } from "@mui/icons-material";
import { Helmet } from "react-helmet";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Axios from "../axios";
import { useAuth } from "../auth/authContext";
import { findNavItemByPath } from "../navigation/navDataUtils";

// Dates are stored as UTC midnight and represent a calendar day, not an instant —
// always read them back via the UTC getters/toISOString so a viewer whose local
// timezone is behind UTC doesn't see the previous day.
const toDateOnly = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString().split("T")[0];
};

const formatDisplayDate = (value) => {
  const dateOnly = toDateOnly(value);
  if (!dateOnly) return "-";
  const [y, m, d] = dateOnly.split("-").map(Number);
  return `${m}/${d}/${y}`;
};

const getExpiryStatus = (diffDays) => {
  if (diffDays === null || diffDays === undefined) return { label: "No Date", color: "default" };
  if (diffDays < 0) return { label: "Expired", color: "error" };
  if (diffDays === 0) return { label: "Expires Today", color: "error" };
  if (diffDays <= 7) return { label: `${diffDays} day${diffDays > 1 ? "s" : ""} left`, color: "warning" };
  return { label: `${diffDays} days left`, color: "success" };
};

const computeDaysUntilExpiry = (raceStartDate) => {
  const dateOnly = toDateOnly(raceStartDate);
  if (!dateOnly) return null;
  const [y, m, d] = dateOnly.split("-").map(Number);
  const startUTC = Date.UTC(y, m - 1, d);
  const today = new Date();
  const todayUTC = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.ceil((startUTC - todayUTC) / (1000 * 60 * 60 * 24));
};

const RaceManagement = () => {
  const { navData } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [write, setWrite] = useState(false);
  const [deleteState, setDeleteState] = useState({ status: false, id: "" });
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [count, setCount] = useState(0);
  const [search, setSearch] = useState("");
  const [sortOrder, setSortOrder] = useState({ name: "", direction: "asc" });
  const BACKEND_URL = process.env.REACT_APP_BACKEND;

  useEffect(() => {
    const currentPath = location.pathname;
    const matchingNavItem = findNavItemByPath(navData, currentPath);
    setWrite(Boolean(matchingNavItem?.write));
  }, [location.pathname, navData]);

  const showError = (err) => {
    toast.error(err?.response?.data?.message || err?.response?.data?.error || "Something went wrong");
  };

  const fetchRaces = useCallback((pageIndex, searchText, sort) => {
    Axios.get(`${BACKEND_URL}race`, {
      params: {
        page: pageIndex + 1,
        limit: 10,
        search: searchText,
        sortField: sort?.name || "",
        sortOrder: sort?.direction || "",
      },
    })
      .then((res) => {
        setRows(res?.data?.data?.docs || []);
        setCount(res?.data?.data?.totalDocs || 0);
      })
      .catch(showError);
  }, [BACKEND_URL]);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchRaces(page, search, sortOrder);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchRaces]);

  const deleteRace = () => {
    setLoading(true);
    Axios.delete(`${BACKEND_URL}race/${deleteState.id}`)
      .then((res) => {
        toast.success(res?.data?.message || "Deleted successfully");
        setDeleteState({ status: false, id: "" });
        fetchRaces(page, search, sortOrder);
      })
      .catch(showError)
      .finally(() => setLoading(false));
  };

  const cloneRace = (id) => {
    setLoading(true);
    Axios.post(`${BACKEND_URL}race/${id}/clone`)
      .then((res) => {
        toast.success(res?.data?.message || "Duplicated successfully");
        fetchRaces(page, search, sortOrder);
      })
      .catch(showError)
      .finally(() => setLoading(false));
  };

  const toggleStatus = (id, currentActive) => {
    setLoading(true);
    const request = currentActive
      ? Axios.patch(`${BACKEND_URL}race/${id}/deactivate`)
      : Axios.patch(`${BACKEND_URL}race/${id}/activate`);

    request
      .then((res) => {
        toast.success(res?.data?.message || "Status updated");
        fetchRaces(page, search, sortOrder);
      })
      .catch(showError)
      .finally(() => setLoading(false));
  };

  const rowsWithExpiry = useMemo(
    () => rows.map((r) => ({ ...r, daysUntilExpiry: computeDaysUntilExpiry(r.startDate) })),
    [rows]
  );

  const columns = [
    {
      name: "heroImage",
      label: "Image",
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (i) =>
          rowsWithExpiry[i]?.heroImage ? (
            <img src={rowsWithExpiry[i].heroImage} alt="" style={{ width: 60, height: 40, objectFit: "cover", borderRadius: 4, backgroundColor: "#f7f7f7" }} />
          ) : (
            "-"
          ),
      },
    },
    { name: "raceName", label: "Race Name" },
    {
      name: "eventLink",
      label: "Event Link",
      options: {
        sort: false,
        customBodyRenderLite: (i) =>
          rowsWithExpiry[i]?.eventLink ? (
            <a href={rowsWithExpiry[i].eventLink} target="_blank" rel="noopener noreferrer">
              View Event
            </a>
          ) : (
            "-"
          ),
      },
    },
    {
      name: "location",
      label: "Location",
      options: {
        customBodyRenderLite: (i) => rowsWithExpiry[i]?.location?.address || rowsWithExpiry[i]?.location || "-",
      },
    },
    { name: "distanceType", label: "Distance Type" },
    { name: "race_type", label: "Race Type" },
    { name: "track_type", label: "Track Type" },
    { name: "spots_left", label: "Spots Left" },
    {
      name: "dates",
      label: "Dates",
      options: {
        customBodyRenderLite: (i) =>
          `${formatDisplayDate(rowsWithExpiry[i]?.startDate)} - ${formatDisplayDate(rowsWithExpiry[i]?.endDate)}`,
      },
    },
    {
      name: "coaches",
      label: "Coaches",
      options: {
        sort: false,
        customBodyRenderLite: (i) => rowsWithExpiry[i]?.coaches?.length || 0,
      },
    },
    {
      name: "status",
      label: "Status",
      options: {
        customBodyRenderLite: (i) => (rowsWithExpiry[i]?.is_active ? "Active" : "Inactive"),
      },
    },
    {
      name: "daysUntilExpiry",
      label: "Expiration",
      options: {
        filter: false,
        customBodyRender: (value) => {
          const status = getExpiryStatus(value);
          return <Chip label={status.label} size="small" color={status.color} />;
        },
      },
    },
    write && {
      name: "actions",
      label: "Actions",
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (i) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Switch
              checked={Boolean(rowsWithExpiry[i]?.is_active)}
              disabled={loading}
              onChange={() => toggleStatus(rowsWithExpiry[i]?._id, rowsWithExpiry[i]?.is_active)}
              size="small"
            />
            <Edit sx={{ color: "#1976d2", fontSize: 20, cursor: "pointer" }} onClick={() => navigate(`/race-management/edit/${rowsWithExpiry[i]?._id}`)} />
            <ReceiptLong
              titleAccess="View Purchases"
              sx={{ color: "#616161", fontSize: 20, cursor: "pointer" }}
              onClick={() => navigate(`/race-management/${rowsWithExpiry[i]?._id}/purchases`)}
            />
            <ContentCopy
              titleAccess="Duplicate"
              sx={{ color: "#616161", fontSize: 18, cursor: loading ? "default" : "pointer", pointerEvents: loading ? "none" : "auto" }}
              onClick={() => cloneRace(rowsWithExpiry[i]?._id)}
            />
            <DeleteOutline
              sx={{ color: "red", fontSize: 20, cursor: "pointer" }}
              onClick={() => setDeleteState({ status: true, id: rowsWithExpiry[i]?._id })}
            />
          </Box>
        ),
      },
    },
  ].filter(Boolean);

  return (
    <div className="container-fluid">
      <Helmet>
        <title>Race Management</title>
      </Helmet>
      <div className="d-flex justify-content-between align-center mb-3">
        <h2>Race Cations</h2>
        {write && (
          <Button variant="contained" onClick={() => navigate("/race-management/add")}>
            Create Race Cation
          </Button>
        )}
      </div>

      <MUIDataTable
        title="Race Cations list"
        data={rowsWithExpiry}
        columns={columns}
        options={{
          responsive: "standard",
          selectableRows: "none",
          filter: false,
          search: true,
          viewColumns: false,
          serverSide: true,
          count,
          page,
          sortOrder,
          rowsPerPageOptions: [],
          download: false,
          onTableChange: (action, tableState) => {
            if (action === "changePage") {
              setPage(tableState.page);
              fetchRaces(tableState.page, search, sortOrder);
            } else if (action === "sort") {
              const newSortOrder = tableState.sortOrder;
              setSortOrder(newSortOrder);
              setPage(0);
              fetchRaces(0, search, newSortOrder);
            }
          },
          onSearchChange: (searchText) => {
            const searchValue = searchText || "";
            setSearch(searchValue);
            setPage(0);
            fetchRaces(0, searchValue, sortOrder);
          },
        }}
      />

      <Dialog open={deleteState.status} onClose={() => setDeleteState({ status: false, id: "" })} fullWidth>
        <div className="d-flex justify-content-between align-center p-3">
          <h3>Delete race cation</h3>
          <IconButton onClick={() => setDeleteState({ status: false, id: "" })}>
            <Close />
          </IconButton>
        </div>
        <DialogContent dividers>Are you sure you want to delete this race?</DialogContent>
        <DialogActions>
          <Button className="cancel-button" onClick={() => setDeleteState({ status: false, id: "" })}>
            Cancel
          </Button>
          <Button className="submit-button" variant="contained" disabled={loading} onClick={deleteRace}>
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default RaceManagement;
